import React, { FC, useCallback, useMemo } from "react";
import "./WsInplantsList.scss";
import { useNavigate, useParams } from "react-router-dom";
import { AppPaths } from "@app/router/paths";
import { useWindows } from "@app/overlay";
import { OPEN_FILTER_QUERY_PARAM } from "@app/router/queryParams";
import { BusinessList, ListItem } from "@src/components/business-list";
import { CardHeader, type CardHeaderButton } from "@shared/ui/card-header";
import { BusinessDivisionValue, BusinessDateValue } from "@src/components/business-data-display";
import { BalanceDocType } from "../../../../../../types/business/balanceDocType";
import { checkBalanceDocType } from "../../../../shared/checkBalanceDocType";
import { useBusinessListQuery } from "@src/hooks/useBusinessListQuery";
import { useUrlFilter } from "@shared/hooks/useUrlFilter";
import { useUrlParamTrigger } from "@shared/hooks/useUrlParamTrigger";
import { wsInplantsServices, type WsInplant, type WsInplantFilter } from "../../../../api/wsInplants";
import { serializeWsInplantApiFilter } from "./helpers/serializeWsInplantApiFilter";
import { useWsInplantsListMutations } from "./useWsInplantsListMutations";
import { wsInplantsListWindows } from "./wsInplantsList.windows";

export const WsInplantsList: FC = () => {
    const { docType } = useParams();
    const windows = useWindows();
    const navigate = useNavigate();
    const filterState = useUrlFilter<WsInplantFilter>();
    const { filter, applyFilter } = filterState;

    const listType = useMemo((): BalanceDocType | null => {
        if (docType == null || !checkBalanceDocType(docType)) {
            return null;
        }
        return docType;
    }, [docType]);

    const hasRightCreateInplant = true; // TODO: добавить проверку прав, когда понадобится

    const list = useBusinessListQuery<WsInplant, WsInplantFilter>({
        queryKey: "workshop.wsInplants",
        extraQueryKey: [listType ?? "unknown"],
        filter,
        queryFn: (flt, signal) =>
            wsInplantsServices.load(
                {
                    flt: serializeWsInplantApiFilter(flt),
                    include: ["Sender", "SenderSection", "Receiver", "ReceiverSection"],
                },
                { signal },
            ),
        getId: (item) => item.Code,
    });

    const { createMutation } = useWsInplantsListMutations({ list });
    const { mutateAsync: createInplant } = createMutation;

    const openFilterWindow = useCallback((): void => {
        if (listType == null) {
            return;
        }
        wsInplantsListWindows.filter.open(windows, {
            listType,
            initialFilter: filter,
            onApply: (flt) => {
                applyFilter(flt, true);
            },
        });
    }, [applyFilter, filter, listType, windows]);

    const handleCreateInplantOpen = useCallback((): void => {
        wsInplantsListWindows.create.open(windows, {
            initialFilter: filter,
            onSubmit: async (values) => {
                const created = await createInplant({ values });
                navigate(AppPaths.WORKSHOP.INPLANTS.CARD(BalanceDocType.Leave, String(created.Code)));
            },
        });
    }, [createInplant, filter, navigate, windows]);

    const headerButtons = useMemo<CardHeaderButton[]>(
        () => [
            {
                type: "button",
                text: "Поиск",
                srcIcon: "/images/common/options.svg",
                onClick: openFilterWindow,
            },
            {
                hide: listType === BalanceDocType.Arrival || !hasRightCreateInplant,
                type: "button",
                text: "Создать",
                srcIcon: "/images/common/plus.svg",
                onClick: handleCreateInplantOpen,
            },
        ],
        [handleCreateInplantOpen, hasRightCreateInplant, listType, openFilterWindow],
    );

    useUrlParamTrigger(OPEN_FILTER_QUERY_PARAM, () => {
        openFilterWindow();
    });

    if (listType == null) {
        throw new Error("Не определено или некорректное значение типа списка Приход/Расход");
    }

    return (
        <div className="ws_inplants_list_page">
            <CardHeader
                text={`ВЗН УП (${listType === BalanceDocType.Arrival ? "Приход" : "Расход"})`}
                buttons={headerButtons}
            />
            <BusinessList
                list={list}
                onOpenFilter={openFilterWindow}
                infinityScroll={{
                    limit: 50,
                    loadMoreScrollTrigger: 70,
                }}
            >
                {list.data.map((item) => (
                    <ListItem
                        key={item.Code}
                        title={`ВЗН №${item.Num}`}
                        props={[
                            {
                                label: "Оправитель",
                                value: <BusinessDivisionValue division={item?.Sender} section={item?.SenderSection} />,
                            },
                            {
                                label: "Получатель",
                                value: <BusinessDivisionValue division={item?.Receiver} section={item?.ReceiverSection} />,
                            },
                            {
                                label: "Дата выдачи",
                                value: <BusinessDateValue value={item.LeaveMoveDate} />,
                            },
                        ]}
                        onClick={() => {
                            navigate({
                                pathname: `${item.Code}`,
                            });
                        }}
                    />
                ))}
            </BusinessList>
        </div>
    );
};
